import React, { useState } from 'react'
import { useTranslation } from 'language/translator'

import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import SearchField from 'components_new/molecules/SearchField'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleTable from 'components_new/molecules/SimpleTable'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import SettingsTemplate from 'components_new/templates/SettingsTemplate'

import AddDataAccessGroupDialog from './dialogs/AddDataAccessGroupDialog'
import EditDataAccessGroupDialog from './dialogs/EditDataAccessGroupDialog'

import {
  useDefaultOrganizationalUnits,
  useFullAccessEnum,
  useOrganizationalUnitsArray,
  useOrganizationalUnitsFetched
} from 'redux/hooks/OrganizationalUnits'
import { useOrganizationalUnitActions } from 'redux/actions/OrganizationalUnits'
import { useAccounts } from 'redux/hooks/Accounts'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import StatusBox from 'components_new/molecules/StatusBox'
import { useAvailableUnits } from 'redux/hooks/AvailableUnits'

const DataAccess = () => {
  const { t, lang } = useTranslation()

  const { tryDeleteOrganizationalUnit } = useOrganizationalUnitActions()
  const organizationalUnits = useOrganizationalUnitsArray()
  const defaultOrganizationalUnits = useDefaultOrganizationalUnits()
  const fullAccessEnum = useFullAccessEnum()
  const availableUnits = useAvailableUnits()
  const fetched = useOrganizationalUnitsFetched()
  const { isCompanyBased } = useAccountSettings()
  const { accounts } = useAccounts()

  /*-- add --*/
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false)

  const handleOpenAdd = () => {
    setAddDialogOpen(true)
  }

  const handleCloseAdd = () => {
    setAddDialogOpen(false)
  }

  /*-- edit --*/
  const [editDialog, setEditDialog] = useState<{
    id: string
    open: boolean
  }>({ id: '', open: false })

  const handleOpenEdit = (id: string) => {
    setEditDialog({
      id,
      open: true
    })
  }

  const handleCloseEdit = () => {
    setEditDialog((prevState) => ({
      ...prevState,
      open: false
    }))
  }

  /*-- edit --*/
  const [deleteDialog, setDeleteDialog] = useState<{
    id: string
    open: boolean
    numberOfUsers: number
  }>({ id: '', open: false, numberOfUsers: 0 })

  const handleOpenDelete = (id: string) => {
    setDeleteDialog({
      id,
      open: true,
      numberOfUsers: accounts.filter((acc) => acc.organizational_unit_id === id)
        .length
    })
  }

  const handleCloseDelete = () => {
    setDeleteDialog((prevState) => ({
      ...prevState,
      open: false
    }))
  }

  const [searchQuery, setSearchQuery] = useState('')
  const filteredOrganizationalUnits = organizationalUnits.filter((unit) =>
    unit.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const rows: { cells: React.JSX.Element[] }[] = []

  defaultOrganizationalUnits.forEach((unit) => {
    rows.push({
      cells: [
        <>
          <b>{unit.name[lang]}</b>
          <Chip label={t('global', 'default')} size="xsmall" sx={{ ml: 1 }} />
        </>,
        <>
          {unit.id === fullAccessEnum ? availableUnits.length : 0}{' '}
          {t('org_units', isCompanyBased ? 'units_company' : 'units', true)}
        </>,
        <>
          <IconButton disabled={true}>
            <Icon name="Edit" />
          </IconButton>
          <IconButton disabled={true}>
            <Icon name="Delete" />
          </IconButton>
        </>
      ]
    })
  })

  filteredOrganizationalUnits.forEach((unit) => {
    rows.push({
      cells: [
        <>
          <b>{unit.name}</b>
        </>,
        <>
          {unit.names.length}{' '}
          {t('org_units', isCompanyBased ? 'units_company' : 'units', true)}
        </>,
        <>
          <IconButton onClick={() => handleOpenEdit(unit.id)}>
            <Icon name="Edit" />
          </IconButton>
          <IconButton onClick={() => handleOpenDelete(unit.id)}>
            <Icon name="Delete" />
          </IconButton>
        </>
      ]
    })
  })

  return (
    <>
      <SettingsTemplate>
        <SectionHeader
          gutterBottom={true}
          title={t('data_access', 'data_access')}
          titleAdornment={
            searchQuery ? (
              <Text color="text.secondary">
                {filteredOrganizationalUnits.length}{' '}
                {t('search', 'results_found', true)}
              </Text>
            ) : null
          }
        />

        {/*-- search --*/}
        <SearchField
          autoFocus={true}
          onClear={() => setSearchQuery('')}
          onSearch={(value) => setSearchQuery(value)}
          size="small"
          sx={{ mb: 1 }}
          value={searchQuery}
        />

        <SimpleTable
          addRow={{
            onClick: handleOpenAdd
          }}
          border={true}
          columns={[
            { title: t('global', 'name') },
            {
              title: t('org_units', isCompanyBased ? 'units_company' : 'units')
            },
            { align: 'right', title: t('global', 'actions') }
          ]}
          loading={!fetched}
          hover={true}
          rows={rows}
          size="small"
        />
      </SettingsTemplate>

      {/*-- dialogs --*/}
      <AddDataAccessGroupDialog onClose={handleCloseAdd} open={addDialogOpen} />

      <EditDataAccessGroupDialog
        id={editDialog.id}
        onClose={handleCloseEdit}
        open={editDialog.open}
      />

      <DeleteDialog
        onClose={handleCloseDelete}
        onDelete={async (cb) => {
          try {
            await tryDeleteOrganizationalUnit(deleteDialog.id)
            handleCloseDelete()
            cb()
          } catch (error) {
            cb()
          }
        }}
        open={deleteDialog.open}
        type={t('data_access', 'data_access', true)}
        unlockPhrase={t('global', 'delete')}
      >
        {deleteDialog.numberOfUsers > 0 ? (
          <StatusBox
            title={t('global', 'note')}
            severity="error"
            sx={{ mb: 2 }}
          >
            {t('data_access', 'users_using_access', false, {
              numberOfUsers: deleteDialog.numberOfUsers.toString()
            })}
          </StatusBox>
        ) : null}
      </DeleteDialog>
    </>
  )
}

export default DataAccess
